<template>
    <router-view />
</template>

<script setup lang="ts">
import 'vue3-toastify/dist/index.css';
</script>

<!--
    Estilos globais do checkout: base dos elementos, grid, formulários e utilitários usados nos templates.
    Os valores seguem os do Bootstrap 5.2.3, que era usado antes, para manter o mesmo visual.
-->
<style lang="scss">
@use 'sass:math';

$breakpoint-sm: 576px;
$breakpoint-md: 768px;
$gutter: 1.5rem;
$body-color: #212529;
$border-radius: 0.375rem;

/* ---------- Base ---------- */
*,
*::before,
*::after {
    box-sizing: border-box;
}

body {
    margin: 0;
    font-family:
        system-ui,
        -apple-system,
        'Segoe UI',
        Roboto,
        'Helvetica Neue',
        'Noto Sans',
        'Liberation Sans',
        Arial,
        sans-serif;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: $body-color;
    background-color: #fff;
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
}

h1,
h2,
h3,
h4,
h5,
h6,
.h3,
.h6 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-weight: 500;
    line-height: 1.2;
}

h1 {
    font-size: calc(1.375rem + 1.5vw);
}

h2 {
    font-size: calc(1.325rem + 0.9vw);
}

h3,
.h3 {
    font-size: calc(1.3rem + 0.6vw);
}

h4 {
    font-size: calc(1.275rem + 0.3vw);
}

h5 {
    font-size: 1.25rem;
}

h6,
.h6 {
    font-size: 1rem;
}

@media (min-width: 1200px) {
    h1 {
        font-size: 2.5rem;
    }

    h2 {
        font-size: 2rem;
    }

    h3,
    .h3 {
        font-size: 1.75rem;
    }

    h4 {
        font-size: 1.5rem;
    }
}

p,
ul,
ol {
    margin-top: 0;
    margin-bottom: 1rem;
}

ul,
ol {
    padding-left: 2rem;
}

ul ul {
    margin-bottom: 0;
}

hr {
    margin: 1rem 0;
    color: inherit;
    border: 0;
    border-top: 1px solid;
    opacity: 0.25;
}

b,
strong {
    font-weight: bolder;
}

small {
    font-size: 0.875em;
}

a {
    color: #0d6efd;
    text-decoration: underline;

    &:hover {
        color: #0a58ca;
    }

    &:not([href]):not([class]),
    &:not([href]):not([class]):hover {
        color: inherit;
        text-decoration: none;
    }
}

figure {
    margin: 0 0 1rem;
}

img,
svg {
    vertical-align: middle;
}

table {
    caption-side: bottom;
    border-collapse: collapse;
}

th {
    text-align: inherit;
    text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
    border-color: inherit;
    border-style: solid;
    border-width: 0;
}

label {
    display: inline-block;
}

button {
    border-radius: 0;

    &:focus:not(:focus-visible) {
        outline: 0;
    }
}

input,
button,
select,
textarea {
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
}

button,
select {
    text-transform: none;
}

select {
    word-wrap: normal;

    &:disabled {
        opacity: 1;
    }
}

[role='button'] {
    cursor: pointer;
}

button,
[type='button'],
[type='submit'] {
    -webkit-appearance: button;

    &:not(:disabled) {
        cursor: pointer;
    }
}

textarea {
    resize: vertical;
}

[hidden] {
    display: none !important;
}

/* ---------- Layout ---------- */
.container {
    width: 100%;
    padding-right: $gutter * 0.5;
    padding-left: $gutter * 0.5;
    margin-right: auto;
    margin-left: auto;

    @media (min-width: $breakpoint-sm) {
        max-width: 540px;
    }

    @media (min-width: $breakpoint-md) {
        max-width: 720px;
    }

    @media (min-width: 992px) {
        max-width: 960px;
    }

    @media (min-width: 1200px) {
        max-width: 1140px;
    }

    @media (min-width: 1400px) {
        max-width: 1320px;
    }
}

.row {
    --gutter-x: #{$gutter};
    display: flex;
    flex-wrap: wrap;
    margin-right: calc(-0.5 * var(--gutter-x));
    margin-left: calc(-0.5 * var(--gutter-x));

    > * {
        flex-shrink: 0;
        width: 100%;
        max-width: 100%;
        padding-right: calc(var(--gutter-x) * 0.5);
        padding-left: calc(var(--gutter-x) * 0.5);
    }
}

.g-0 {
    --gutter-x: 0;
}

.col {
    flex: 1 0 0%;
}

@each $size in 2, 3, 6, 9, 10, 12 {
    .col-#{$size} {
        flex: 0 0 auto;
        width: math.percentage(math.div($size, 12));
    }
}

@media (min-width: $breakpoint-md) {
    .col-md {
        flex: 1 0 0%;
    }

    @each $size in 2, 4, 6, 8, 10, 12 {
        .col-md-#{$size} {
            flex: 0 0 auto;
            width: math.percentage(math.div($size, 12));
        }
    }
}

/* ---------- Formulários ---------- */
.form-control {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: $body-color;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    appearance: none;
    border-radius: $border-radius;
    transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

    &:focus {
        color: $body-color;
        background-color: #fff;
        border-color: #86b7fe;
        outline: 0;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }

    &::placeholder {
        color: #6c757d;
        opacity: 1;
    }

    &:disabled {
        background-color: #e9ecef;
        opacity: 1;
    }

    &.is-invalid {
        border-color: #dc3545;
        padding-right: calc(1.5em + 0.75rem);
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right calc(0.375em + 0.1875rem) center;
        background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);

        &:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
        }
    }
}

.invalid-feedback {
    display: none;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875em;
    color: #dc3545;
}

.is-invalid ~ .invalid-feedback {
    display: block;
}

.input-group {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;

    > .form-control {
        position: relative;
        flex: 1 1 auto;
        width: 1%;
        min-width: 0;

        &:focus {
            z-index: 5;
        }
    }

    .btn {
        position: relative;
        z-index: 2;
    }

    > :not(:last-child):not(.invalid-feedback) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    > :not(:first-child):not(.invalid-feedback) {
        margin-left: -1px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
}

.btn {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: $body-color;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: $border-radius;
    transition:
        color 0.15s ease-in-out,
        background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;

    &:disabled {
        pointer-events: none;
        opacity: 0.65;
    }
}

.btn-outline-secondary {
    color: #6c757d;
    border-color: #6c757d;

    &:hover,
    &:active {
        color: #fff;
        background-color: #6c757d;
        border-color: #6c757d;
    }

    &:focus-visible {
        outline: 0;
        box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.5);
    }

    &:disabled {
        color: #6c757d;
        background-color: transparent;
    }
}

/* ---------- Abas de pagamento ---------- */
.nav {
    display: flex;
    flex-wrap: wrap;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
}

.nav-tabs {
    border-bottom: 1px solid #dee2e6;
}

.tab-content > .tab-pane {
    display: none;
}

.tab-content > .active {
    display: block;
}

.fade {
    transition: opacity 0.15s linear;

    &:not(.in) {
        opacity: 0;
    }
}

/* ---------- Loading ---------- */
@keyframes spinner-border {
    to {
        transform: rotate(360deg);
    }
}

.spinner-border {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    vertical-align: -0.125em;
    border: 0.25em solid currentcolor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: 0.75s linear infinite spinner-border;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.2em;
}

@keyframes placeholder-glow {
    50% {
        opacity: 0.2;
    }
}

.placeholder {
    display: inline-block;
    min-height: 1em;
    vertical-align: middle;
    cursor: wait;
    background-color: currentcolor;
    opacity: 0.5;
}

.placeholder-glow .placeholder {
    animation: placeholder-glow 2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
    .form-control,
    .btn,
    .fade {
        transition: none;
    }

    .spinner-border {
        animation-duration: 1.5s;
    }
}

/* ---------- Utilitários ---------- */
.visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
}

.img-fluid {
    max-width: 100%;
    height: auto;
}

.d-block {
    display: block !important;
}

.d-flex {
    display: flex !important;
}

.d-none {
    display: none !important;
}

.position-relative {
    position: relative !important;
}

.w-100 {
    width: 100% !important;
}

.flex-column {
    flex-direction: column !important;
}

.flex-grow-1 {
    flex-grow: 1 !important;
}

.justify-content-end {
    justify-content: flex-end !important;
}

.justify-content-center {
    justify-content: center !important;
}

.justify-content-between {
    justify-content: space-between !important;
}

.align-items-center {
    align-items: center !important;
}

.text-center {
    text-align: center !important;
}

.text-primary {
    color: #0d6efd !important;
}

.text-danger {
    color: #dc3545 !important;
}

.rounded-2 {
    border-radius: $border-radius !important;
}

$spacers: (
    0: 0,
    1: 0.25rem,
    2: 0.5rem,
    3: 1rem,
    4: 1.5rem,
);

@each $key, $value in $spacers {
    .m-#{$key} {
        margin: $value !important;
    }

    .mt-#{$key} {
        margin-top: $value !important;
    }

    .mb-#{$key} {
        margin-bottom: $value !important;
    }

    .me-#{$key} {
        margin-right: $value !important;
    }

    .my-#{$key} {
        margin-top: $value !important;
        margin-bottom: $value !important;
    }

    .pt-#{$key} {
        padding-top: $value !important;
    }

    .pe-#{$key} {
        padding-right: $value !important;
    }

    .ps-#{$key} {
        padding-left: $value !important;
    }

    .gap-#{$key} {
        gap: $value !important;
    }
}

.mx-auto {
    margin-right: auto !important;
    margin-left: auto !important;
}

@media (min-width: $breakpoint-sm) {
    .d-sm-block {
        display: block !important;
    }

    .d-sm-none {
        display: none !important;
    }
}

@media (min-width: $breakpoint-md) {
    .d-md-flex {
        display: flex !important;
    }

    .flex-md-row {
        flex-direction: row !important;
    }

    .align-items-md-start {
        align-items: flex-start !important;
    }

    .text-md-start {
        text-align: left !important;
    }
}
</style>
